
import React, { useState, useEffect } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { 
  BarChart3, 
  Wifi, 
  AlertTriangle, 
  Users, 
  Plus, 
  Shield, 
  LayoutDashboard,
  ChevronLeft,
  ChevronRight,
  CheckCircle2,
  XCircle,
  Network,
  Brain
} from 'lucide-react';
import { cn } from '@/lib/utils';
import { useMobile } from '@/hooks/use-mobile';
import { Button } from '@/components/ui/button';

type NavItem = {
  name: string;
  href: string;
  icon: React.ElementType;
};

const navItems: NavItem[] = [
  { name: 'Command Center', href: '/', icon: LayoutDashboard },
  { name: 'Networks', href: '/networks', icon: Network },
  { name: 'AI Automation', href: '/ai-automation', icon: Brain },
  { name: 'Devices', href: '/devices', icon: Wifi },
  { name: 'Users', href: '/users', icon: Users },
  { name: 'Add Device', href: '/add-device', icon: Plus },
  { name: 'Security', href: '/security', icon: Shield },
  { name: 'Alerts', href: '/alerts', icon: AlertTriangle },
];

const Sidebar = () => {
  const [collapsed, setCollapsed] = useState(false);
  const [networkStatus, setNetworkStatus] = useState<'operational' | 'error'>('operational');
  const location = useLocation();
  const navigate = useNavigate();
  const isMobile = useMobile();
  
  useEffect(() => {
    const checkNetworkStatus = () => {
      const hasError = Math.random() < 0.1;
      setNetworkStatus(hasError ? 'error' : 'operational');
    };

    checkNetworkStatus();

    const interval = setInterval(checkNetworkStatus, 30000);
    
    return () => clearInterval(interval);
  }, []);
  
  useEffect(() => {
    const handleResize = () => {
      if (window.innerWidth < 768 && window.innerWidth > window.innerHeight) {
        setCollapsed(true);
      }
    };
    
    window.addEventListener('resize', handleResize);
    handleResize();
    
    return () => window.removeEventListener('resize', handleResize);
  }, []);
  
  if (isMobile) {
    return null;
  }
  
  return (
    <div 
      className={cn(
        "h-[calc(100vh-64px)] border-r border-border bg-sidebar transition-all duration-300 ease-in-out sticky top-16",
        collapsed ? "w-16" : "w-64"
      )}
    >
      <div className="py-4 flex flex-col h-full">
        <div className="px-3 py-2">
          <button
            onClick={() => setCollapsed(!collapsed)}
            className="p-2 rounded-full hover:bg-sidebar-accent flex items-center justify-center w-full transition-colors"
            aria-label={collapsed ? "Expand sidebar" : "Collapse sidebar"}
          >
            {collapsed ? <ChevronRight className="h-4 w-4 text-sidebar-foreground" /> : <ChevronLeft className="h-4 w-4 text-sidebar-foreground" />}
          </button>
        </div>
        
        <nav className="space-y-1 px-3 flex-1 overflow-y-auto scrollbar-none">
          {navItems.map((item) => {
            const isActive = location.pathname === item.href;
            return (
              <Link
                key={item.name}
                to={item.href}
                className={cn(
                  "flex items-center px-3 py-2 text-sm font-medium rounded-md transition-all",
                  isActive
                    ? "bg-primary text-primary-foreground"
                    : "text-sidebar-foreground hover:bg-primary hover:text-primary-foreground"
                )}
                aria-label={collapsed ? item.name : undefined}
              >
                <item.icon className={cn("h-5 w-5 flex-shrink-0", collapsed ? "mx-auto" : "mr-3")} />
                {!collapsed && <span>{item.name}</span>}
              </Link>
            );
          })}
        </nav>
        
        <div className="px-3 py-4 mt-auto">
          {!collapsed && (
            <div className={cn(
              "rounded-lg p-3 text-xs",
              networkStatus === 'operational' 
                ? "bg-green-500/10 border border-green-500/20" 
                : "bg-red-500/10 border border-red-500/20"
            )}>
              <div className="flex items-center justify-between mb-1">
                <p className="font-medium text-sidebar-foreground">Network Status</p>
                {networkStatus === 'operational' ? (
                  <CheckCircle2 className="h-4 w-4 text-green-500" />
                ) : (
                  <XCircle className="h-4 w-4 text-red-500" />
                )}
              </div>
              <p>{networkStatus === 'operational' ? 'All systems operational' : 'System error detected'}</p>
              
              {networkStatus === 'error' && (
                <Button 
                  variant="outline"
                  size="sm"
                  className="w-full mt-2 border-red-500/20 hover:bg-red-500/20 text-red-500"
                  onClick={() => navigate('/alerts')}
                >
                  <AlertTriangle className="h-3 w-3 mr-1" />
                  View Alerts
                </Button>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default Sidebar;
